import { DrawerWrapper } from '@/components/common/Drawer/Drawer'
import { TableLoadingOverlay } from '@/components/common/Table/TableLoadingOverlay'
import { ActiveManagementFiltersContainer } from '@/features/ActiveManagement/components/ActiveManagementFilters/ActiveManagementFilters'
import { IntervalReport } from '@/features/ActiveManagement/components/Interval/IntervalReport'
import { IntervalDrawer } from '@/features/ActiveManagement/components/IntervalDrawer/IntervalDrawer'
import {
    GetReportResponse,
    IntervalReportSettings,
    useElementWidth,
    useFiltersStore,
} from '@/hooks'
import useNavigationGuardStore from '@/stores/useNavigationGuardStore/useNavigationGuardStore'
import {
    DEFAULT_ACTIVE_MANAGEMENT_FILTERING,
    DEFAULT_INTERVAL_REPORT_SETTINGS,
} from '@/utils/constants'
import {
    ComponentProps,
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import {
    hasNameChanged,
    haveFiltersChanged,
    haveSettingsChanged,
} from '../../utils/detectReportChange'
import { ReportEditHeader } from './ReportEditHeader'
import { mapActiveManagementFiltersToParams } from './mapActiveManagementFiltersToParams'
import { mapFiltersToParams } from './mapFiltersToParams'
import { mapParamsToFilters } from './mapParamsToFilters'

type ReportEditIntervalDataProps = {
    data: GetReportResponse
    isOpen: boolean
    setIsOpen: Dispatch<SetStateAction<boolean>>
}

type OnChangeFn = ComponentProps<
    typeof ActiveManagementFiltersContainer
>['onChange']

export const ReportEditIntervalData: React.FC<ReportEditIntervalDataProps> = ({
    data: initialData,
    isOpen,
    setIsOpen,
}) => {
    const { t } = useTranslation('translations')

    const remappedInitialData = useMemo<
        GetReportResponse & { settings: IntervalReportSettings }
    >(
        () => ({
            ...initialData,
            settings:
                initialData.settings &&
                typeof initialData.settings === 'object' &&
                'timezone' in initialData.settings
                    ? {
                          ...initialData.settings,
                          display:
                              initialData.settings.type === 'utilityData'
                                  ? 'meters'
                                  : 'subMeters',
                      }
                    : DEFAULT_INTERVAL_REPORT_SETTINGS,
        }),
        [initialData]
    )
    const [filters, setFilters] = useState(remappedInitialData.filters)
    const [settings, setSettings] = useState<IntervalReportSettings>(
        remappedInitialData.settings as IntervalReportSettings
    )
    const [name, setName] = useState(remappedInitialData.name)
    const [isUpdatePending, setIsUpdatePending] = useState(false)
    const rootNode = useFiltersStore((state) => state.rootNode)

    const toggleButtonRef = useRef<HTMLButtonElement>(null)
    const toggleWidth = useElementWidth(toggleButtonRef)

    const parsedFilters = mapParamsToFilters(filters)

    const baselineFilters = useMemo(
        () =>
            mapActiveManagementFiltersToParams({
                ...DEFAULT_ACTIVE_MANAGEMENT_FILTERING,
                node: rootNode?.nodeId !== undefined ? [rootNode.nodeId] : [],
            }),
        [rootNode?.nodeId]
    )
    const handleResetFilters = useCallback(() => {
        setFilters(baselineFilters)
    }, [baselineFilters])

    const showResetFiltersButton = useMemo(() => {
        return haveFiltersChanged(filters, baselineFilters)
    }, [filters, baselineFilters])

    const updatedReport = useMemo<GetReportResponse>(
        () => ({
            ...remappedInitialData,
            name,
            filters,
            settings,
        }),
        [remappedInitialData, filters, name, settings]
    )

    const setUnsavedChanges = useNavigationGuardStore(
        (state) => state.setUnsavedChanges
    )

    useEffect(() => {
        const filtersChanged = haveFiltersChanged(
            filters,
            remappedInitialData.filters
        )
        const settingsChanged = haveSettingsChanged(
            settings,
            remappedInitialData.settings
        )
        const nameChanged = hasNameChanged(name, remappedInitialData.name)

        setUnsavedChanges(filtersChanged || nameChanged || settingsChanged)
    }, [
        name,
        filters,
        settings,
        remappedInitialData.name,
        remappedInitialData.filters,
        remappedInitialData.settings,
        setUnsavedChanges,
    ])

    const handleReportSaved = () => {
        setUnsavedChanges(false)
    }

    const setReportSettings = useCallback(
        (newSettings: Partial<IntervalReportSettings>) => {
            const typeSetting: Partial<IntervalReportSettings> = {}

            if (newSettings.display) {
                typeSetting['type'] =
                    newSettings.display === 'meters'
                        ? 'utilityData'
                        : 'subMeterData'
            }

            setSettings((prevSettings) => ({
                ...prevSettings,
                ...newSettings,
                ...typeSetting,
            }))
        },
        []
    )

    const showResetReportSettingsButton = useMemo(() => {
        return haveSettingsChanged(settings, DEFAULT_INTERVAL_REPORT_SETTINGS)
    }, [settings])

    const onFiltersChange = useCallback<OnChangeFn>((filter, value) => {
        setFilters((prevParams) => {
            const prevFilters = mapParamsToFilters(prevParams)
            const newFilters = {
                ...prevFilters,
                [filter]: value,
            }
            const mappedFiltersToParams = mapFiltersToParams(newFilters)

            const { commodityTypeId: _commodityTypeId, ...mappedFilters } =
                mappedFiltersToParams
            return mappedFilters
        })
    }, [])

    const selectedFilter = settings.display === 'meters' ? 'meter' : 'subMeter'

    const calculationMethodOffset = isOpen ? 16 : toggleWidth

    return (
        <div className="relative grid h-full grid-cols-[100%] grid-rows-[auto_1fr]">
            <div className="relative z-30">
                <ReportEditHeader
                    initialReportName={remappedInitialData.name}
                    report={updatedReport}
                    onChangeName={setName}
                    onSaveAsNewReportSuccess={handleReportSaved}
                    onUpdatePendingChange={setIsUpdatePending}
                />
                <div className="border-b border-neutral-200 p-6">
                    <ActiveManagementFiltersContainer
                        activeFilters={parsedFilters}
                        onChange={onFiltersChange}
                        handleResetFilters={handleResetFilters}
                        showResetFiltersButton={showResetFiltersButton}
                        selectedFilter={selectedFilter}
                    />
                </div>
            </div>
            <TableLoadingOverlay
                show={isUpdatePending}
                title={t('reporting.update_report.loading_save')}
                coverContainer
            />
            <DrawerWrapper className="h-full">
                <IntervalDrawer
                    activeFilters={parsedFilters}
                    reportSettings={settings}
                    setReportSettings={setReportSettings}
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                    toggleButtonRef={toggleButtonRef}
                    showResetReportSettingsButton={
                        showResetReportSettingsButton
                    }
                />
                <IntervalReport
                    activeFilters={parsedFilters}
                    reportSettings={settings}
                    setReportSettings={setReportSettings}
                    isOpen={isOpen}
                    calculationMethodOffset={calculationMethodOffset}
                />
            </DrawerWrapper>
        </div>
    )
}
